/**
 * 🇵🇭 BIR Alphalist .dat File Compiler Utility
 * Generates official BIR-compliant .dat files for forms 1604-C, 1604-F, and 1604-E.
 */

export interface AlphalistHeader {
  formType: '1604-C' | '1604-F' | '1604-E'
  withholdingAgentTin: string // 9 digits
  branchCode: string // 4 digits e.g. "0000"
  rdoCode: string // 3 digits e.g. "047"
  taxableYear: number
  agentName: string
}

export interface PayeeRecord {
  tin: string
  branchCode: string
  name: string // Last Name, First Name Middle Name or Company Name
  atcCode: string // Aliquot Tax Code e.g. "WI100" or "WC100"
  grossPayment: number
  taxRatePercent: number
  taxWithheld: number
}

export class AlphalistCompiler {
  /**
   * Compiles header and payee records into official BIR .dat format line blocks.
   */
  static compileDatFile(header: AlphalistHeader, payees: PayeeRecord[]): string {
    const lines: string[] = []

    // 1. Header Record (H1604C / H1604F / H1604E)
    const headerPrefix = `H${header.formType.replace('-', '')}`
    const cleanTin = header.withholdingAgentTin.replace(/[^0-9]/g, '').padStart(9, '0')
    const cleanBranch = header.branchCode.padStart(4, '0')
    const cleanRdo = header.rdoCode.padStart(3, '0')
    const cleanYear = header.taxableYear.toString()
    const cleanName = header.agentName.toUpperCase().slice(0, 100)

    lines.push(`${headerPrefix},${cleanTin},${cleanBranch},${cleanRdo},${cleanYear},"${cleanName}"`)

    // 2. Detail Records (D1604C / D1604F / D1604E)
    const detailPrefix = `D${header.formType.replace('-', '')}`
    payees.forEach((p, idx) => {
      const seqNo = (idx + 1).toString().padStart(5, '0')
      const payeeTin = p.tin.replace(/[^0-9]/g, '').padStart(9, '0')
      const payeeBranch = (p.branchCode || '0000').padStart(4, '0')
      const payeeName = p.name.toUpperCase().slice(0, 100)
      const atc = (p.atcCode || 'WI100').toUpperCase()
      const gross = p.grossPayment.toFixed(2)
      const rate = p.taxRatePercent.toFixed(2)
      const withheld = p.taxWithheld.toFixed(2)

      lines.push(
        `${detailPrefix},${seqNo},${cleanTin},${cleanBranch},${cleanYear},${payeeTin},${payeeBranch},"${payeeName}",${atc},${gross},${rate},${withheld}`
      )
    })

    // 3. Control Record (C1604C / C1604F / C1604E)
    const controlPrefix = `C${header.formType.replace('-', '')}`
    const totalCount = payees.length.toString()
    const totalGross = payees.reduce((acc, p) => acc + p.grossPayment, 0).toFixed(2)
    const totalWithheld = payees.reduce((acc, p) => acc + p.taxWithheld, 0).toFixed(2)

    lines.push(`${controlPrefix},${cleanTin},${cleanBranch},${cleanYear},${totalCount},${totalGross},${totalWithheld}`)

    return lines.join('\r\n')
  }

  /**
   * Validates TIN format (Philippines standard 9-digit or 12-digit).
   */
  static validateTin(tin: string): boolean {
    const numericTin = tin.replace(/[^0-9]/g, '')
    return numericTin.length === 9 || numericTin.length === 12
  }
}
